import { create } from "zustand";
import {
  registerUser as apiRegisterUser,
  fetchUsers as apiFetchUsers,
  deleteUser as apiDeleteUser,
  updateUserRole as apiUpdateUserRole,
  fetchRoles as apiFetchRoles,
  fetchPermissions as apiFetchPermissions,
  updateRolePermissions as apiUpdateRolePermissions,
  createRole as apiCreateRole,
  deleteRole as apiDeleteRole,
  fetchCrmManagers as apiFetchCrmManagers,
  updateUserCrmManager as apiUpdateUserCrmManager,
  updateUserTeamLead as apiUpdateUserTeamLead,
} from "../services/adminService";

import { useErrorStore } from "components";

// Глобальний стор (state manager) сторінки "Панель адміністратора"
export const useAdminStore = create((set, get) => ({
  users: [],
  roles: [],
  permissions: [],
  crmManagers: [],
  isLoading: true,

  // Ініціалізує стор при першому завантаженні сторінки
  init: async () => {
    // Запобігаємо повторній ініціалізації, якщо дані вже завантажені
    if (get().users.length > 0 && !get().isLoading) return;

    set({ isLoading: true });
    try {
      // Promise.all виконує всі запити паралельно
      const [usersData, rolesData, permissionsData, crmManagersData] = await Promise.all([
        get().fetchUsers(),
        get().fetchRoles(),
        get().fetchPermissions(),
        get().fetchCrmManagers(),
      ]);

      // Оновлюємо стан ЛИШЕ ОДИН РАЗ, коли всі дані готові
      set({
        users: usersData,
        roles: rolesData,
        permissions: permissionsData,
        crmManagers: crmManagersData,
      });
    } catch (err) {
      console.error("Помилка під час ініціалізації:", err);
    } finally {
      set({ isLoading: false });
    }
  },

  // Асинхронно завантажує список користувачів
  fetchUsers: async () => {
    try {
      return await apiFetchUsers();
    } catch (err) {
      console.error("Помилка завантаження користувачів:", err);
      return [];
    }
  },

  // Асинхронно завантажує список ролей
  fetchRoles: async () => {
    try {
      return await apiFetchRoles();
    } catch (err) {
      console.error("Помилка завантаження ролей:", err);
      return [];
    }
  },

  // Асинхронно завантажує список дозволів
  fetchPermissions: async () => {
    try {
      return await apiFetchPermissions();
    } catch (err) {
      console.error("Помилка завантаження дозволів:", err);
      return [];
    }
  },

  // Реєструє нового користувача
  registerUser: async (userData) => {
    try {
      await apiRegisterUser(userData);

      // Після успішної реєстрації оновлюємо список користувачів
      const freshUsers = await get().fetchUsers();
      set({ users: freshUsers });
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError(`Не вдалося створити користувача: ${err.message}`);
      // Вивід помилки в консоль
      console.error("Помилка реєстрації користувача:", err);

      throw err;
    }
  },

  // Оптимістично видаляє користувача
  removeUser: async (userId) => {
    const previousUsers = get().users;

    // Миттєво оновлюємо UI, не чекаючи відповіді сервера
    set({ users: previousUsers.filter((user) => user.id !== userId) });

    // Видаляємо користувача
    try {
      await apiDeleteUser(userId);
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError("Не вдалося видалити користувача. Зміни скасовано.");
      // Вивід помилки в консоль
      console.error("Помилка видалення користувача:", err);

      // Якщо сталася помилка, повертаємо UI до попереднього стану
      set({ users: previousUsers });
    }
  },

  // Оптимістично оновлює роль для конкретного користувача
  updateUserRole: async (userId, newRoleId) => {
    const previousUsers = get().users;
    // Миттєво оновлюємо UI, не чекаючи відповіді сервера
    set({
      users: previousUsers.map((user) =>
        user.id === userId ? { ...user, role_id: newRoleId } : user
      ),
    });

    // Оновлюємо роль на сервері
    try {
      await apiUpdateUserRole(userId, newRoleId);
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError("Не вдалося оновити роль. Зміни скасовано.");
      // Вивід помилки в консоль
      console.error("Помилка оновлення ролі:", err);

      // Якщо сталася помилка, повертаємо UI до попереднього стану
      set({ users: previousUsers });
    }
  },

  // Асинхронно завантажує менеджерів CRM (для прив'язки користувача)
  fetchCrmManagers: async () => {
    try {
      return await apiFetchCrmManagers();
    } catch (err) {
      console.error("Помилка завантаження менеджерів CRM:", err);
      return [];
    }
  },

  // Оптимістично прив'язує користувача до менеджера CRM (null — відв'язує)
  updateUserCrmManager: async (userId, crmManagerId) => {
    const previousUsers = get().users;
    set({
      users: previousUsers.map((user) =>
        user.id === userId ? { ...user, crm_manager_id: crmManagerId } : user
      ),
    });

    try {
      await apiUpdateUserCrmManager(userId, crmManagerId);
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError("Не вдалося оновити менеджера CRM. Зміни скасовано.");
      // Вивід помилки в консоль
      console.error("Помилка оновлення менеджера CRM:", err);

      set({ users: previousUsers });
    }
  },

  // Оптимістично призначає користувачу тімліда (null — без команди)
  updateUserTeamLead: async (userId, teamLeadId) => {
    const previousUsers = get().users;
    set({
      users: previousUsers.map((user) =>
        user.id === userId ? { ...user, team_lead_id: teamLeadId } : user
      ),
    });

    try {
      await apiUpdateUserTeamLead(userId, teamLeadId);
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError("Не вдалося оновити тімліда. Зміни скасовано.");
      // Вивід помилки в консоль
      console.error("Помилка оновлення тімліда:", err);

      set({ users: previousUsers });
    }
  },

  // Оновлює дозволи для конкретної ролі
  updateRolePermissions: async (roleId, permissionIds) => {
    const previousRoles = get().roles;
    set({
      roles: previousRoles.map((role) => {
        if (role.id === roleId) {
          // Створюємо новий масив дозволів для оновленої ролі
          const newPermissions = get().permissions.filter((p) =>
            permissionIds.includes(p.id)
          );
          return { ...role, permissions: newPermissions };
        }
        return role;
      }),
    });

    try {
      await apiUpdateRolePermissions(roleId, permissionIds);
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError("Не вдалося оновити дозволи. Зміни скасовано.");
      // Вивід помилки в консоль
      console.error("Помилка оновлення дозволів:", err);

      set({ roles: previousRoles });
    }
  },
  createRole: async (roleData) => {
    try {
      // Викликаємо API для створення ролі на сервері
      await apiCreateRole(roleData);

      // Після успішного створення, перезавантажуємо список ролей
      const freshRoles = await get().fetchRoles();
      set({ roles: freshRoles });
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError(`Не вдалося створити роль: ${err.message}`);
      // Вивід помилки в консоль
      console.error("Помилка створення ролі:", err);

      throw err;
    }
  },

  // Видаляє роль
  deleteRole: async (roleId) => {
    try {
      // Викликаємо API для видалення ролі на сервері
      await apiDeleteRole(roleId);

      // Після успішного видалення, перезавантажуємо список ролей
      const freshRoles = await get().fetchRoles();
      set({ roles: freshRoles });
    } catch (err) {
      // Вивід помилки в GUI
      const { showError } = useErrorStore.getState();
      showError(`Не вдалося видалити роль: ${err.message}`);
      // Вивід помилки в консоль
      console.error("Помилка видалення ролі:", err);

      throw err;
    }
  },
}));
